<template>
  <slot />
  <PriceChangeConfirmationModal
    v-if="pending"
    :model-value="isOpen"
    :pending="pending"
    :is-saving="isSaving"
    :error="error"
    @confirm="handleConfirm"
    @cancel="handleCancel"
  />
</template>

<script setup lang="ts">
import type { Booking } from '@/entities/bookings/booking.entity';
import { computed, nextTick, provide, ref, shallowRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUpdateBooking } from '@/queries/use-bookings';
import { Alert } from '@/services/alert.service';
import { isZero } from '@/utils/is-zero';
import PriceChangeConfirmationModal from './PriceChangeConfirmationModal.vue';
import {
  buildPersistPayload,
  dryRunCancellationFee,
  needsPostHandoverCharge,
  needsPriceChangeModal,
  priceChangeConfirmationKey,
  resolveAllowKeepOldPrice,
  resolveCounterparty,
  quoteTotal,
  type ConfirmAndSaveArgs,
  type ConfirmAndSaveResult,
  type ModalSubmit,
  type PendingConfirmation,
} from './price-change-confirmation';

const { t } = useI18n();
const { mutateAsync: updateBooking } = useUpdateBooking();

const isOpen = ref(false);
const isSaving = ref(false);
const isConfirming = ref(false);
const error = ref<string | null>(null);
const pending = shallowRef<PendingConfirmation | null>(null);

let pendingResolve: ((result: ConfirmAndSaveResult) => void) | null = null;

const finish = (result: ConfirmAndSaveResult) => {
  if (!pendingResolve) return;
  const resolve = pendingResolve;
  pendingResolve = null;
  isOpen.value = false;
  error.value = null;
  resolve(result);
};

const reset = () => {
  if (pendingResolve) finish({ status: 'cancelled' });
  isOpen.value = false;
  error.value = null;
};

const persist = (pendingConfirmation: PendingConfirmation, submit: ModalSubmit) => {
  const {
    booking,
    dryRunBooking,
    priceDifferenceCounterparty: fixedCounterparty,
  } = pendingConfirmation;
  const counterparty = resolveCounterparty(
    booking,
    dryRunBooking,
    fixedCounterparty,
    submit.priceDifferenceCounterparty,
  );
  const surchargeAmount = quoteTotal(dryRunBooking) - quoteTotal(booking);
  const charge = needsPostHandoverCharge(booking, dryRunBooking, submit.keptOldPrice, counterparty);

  const { booking: bookingDto, query } = buildPersistPayload(
    pendingConfirmation,
    {
      ...submit,
      storedPaymentMethodId: charge ? submit.storedPaymentMethodId : undefined,
    },
    submit.keptOldPrice ? undefined : counterparty,
    charge && !isZero(surchargeAmount) ? surchargeAmount : undefined,
  );

  return updateBooking({ id: booking.id, booking: bookingDto, query });
};

const confirmAndSave = async ({
  booking,
  updateBookingDto,
  confirmOnlyWhenCancellationFee,
  handoverAdjustment,
  ...rest
}: ConfirmAndSaveArgs): Promise<ConfirmAndSaveResult> => {
  if (pendingResolve) return { status: 'cancelled' };

  const query = handoverAdjustment ? { handoverAdjustment: true as const } : undefined;

  isConfirming.value = true;
  let dryRunBooking: Booking;
  try {
    dryRunBooking = await updateBooking({
      id: booking.id,
      booking: { ...(updateBookingDto ?? {}), dryRun: true },
      query,
    });
  } catch {
    Alert.fire({ titleText: t('dryRunFailedTitle'), text: t('dryRunFailedText'), icon: 'error' });
    return { status: 'cancelled' };
  } finally {
    isConfirming.value = false;
  }

  const pendingConfirmation: PendingConfirmation = {
    booking,
    updateBookingDto,
    confirmOnlyWhenCancellationFee,
    handoverAdjustment,
    dryRunBooking,
    ...rest,
    allowKeepOldPrice: resolveAllowKeepOldPrice(booking, updateBookingDto, rest.allowKeepOldPrice),
  };

  if (
    !needsPriceChangeModal(
      booking,
      dryRunBooking,
      updateBookingDto,
      confirmOnlyWhenCancellationFee,
      rest.priceDifferenceCounterparty,
    )
  ) {
    isSaving.value = true;
    try {
      const savedBooking = await persist(pendingConfirmation, {
        keptOldPrice: false,
        selectedCancellationFee: dryRunCancellationFee(dryRunBooking),
        priceDifferenceCounterparty: resolveCounterparty(
          booking,
          dryRunBooking,
          rest.priceDifferenceCounterparty,
        ),
      });
      return { status: 'saved', savedBooking };
    } catch {
      Alert.fire({ titleText: t('saveFailedTitle'), text: t('saveFailedText'), icon: 'error' });
      return { status: 'failed' };
    } finally {
      isSaving.value = false;
    }
  }

  return new Promise((resolve) => {
    pendingResolve = resolve;
    pending.value = pendingConfirmation;
    nextTick(() => {
      isOpen.value = true;
    });
  });
};

const handleConfirm = async (submit: ModalSubmit) => {
  if (!pending.value || isSaving.value) return;

  isSaving.value = true;
  error.value = null;
  try {
    const savedBooking = await persist(pending.value, submit);
    finish({ status: 'saved', savedBooking });
  } catch {
    error.value = t('saveFailedText');
  } finally {
    isSaving.value = false;
  }
};

const handleCancel = () => {
  if (isSaving.value) return;
  finish({ status: 'cancelled' });
};

provide(priceChangeConfirmationKey, {
  isBusy: computed(() => isConfirming.value || isSaving.value),
  confirmAndSave,
  reset,
});
</script>

<i18n lang="json">
{
  "en": {
    "dryRunFailedTitle": "Price calculation failed",
    "dryRunFailedText": "Could not calculate the new price. Please try again.",
    "saveFailedTitle": "Save failed",
    "saveFailedText": "Could not save the booking changes. Please try again."
  },
  "de": {
    "dryRunFailedTitle": "Preisberechnung fehlgeschlagen",
    "dryRunFailedText": "Der neue Preis konnte nicht berechnet werden. Bitte versuchen Sie es erneut.",
    "saveFailedTitle": "Speichern fehlgeschlagen",
    "saveFailedText": "Die Buchungsänderungen konnten nicht gespeichert werden. Bitte versuchen Sie es erneut."
  },
  "es": {
    "dryRunFailedTitle": "Error al calcular el precio",
    "dryRunFailedText": "No se pudo calcular el nuevo precio. Inténtelo de nuevo.",
    "saveFailedTitle": "Error al guardar",
    "saveFailedText": "No se pudieron guardar los cambios de la reserva. Inténtelo de nuevo."
  }
}
</i18n>
